import React, { Ref, useState } from 'react';
import {
  ActionTypes,
  ObtainRewardAction,
  RuleAttributes,
  RuleType,
  TransactionAction,
  TransactionFixedImpact,
} from '@loyalty/shared-common/rule-api-client';
import { Box, TableCell, TableRow } from '@mui/material';
import {
  DraggableProvidedDragHandleProps,
  DraggableProvidedDraggableProps,
} from 'react-beautiful-dnd';
import { Feature, extractErrorMessage, getLocalizedProperty } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  QlfConfirmationDialog,
  colorPalette,
  useAlerts,
  useConfirmationDialog,
} from '@qualifio/ui-commons';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BurningRuleModal } from './BurningRuleModal';
import { CopyIconButton, DeleteIconButton, EditIconButton } from '../shared/TableIconButton';
import { CurrencyImage } from '../shared/currency/CurrencyImage';
import { Loader } from '../shared/layout/Loader';
import { RuleConflictModal, RuleConflictModalProps } from '../rules/components/RuleConflictModal';
import { RuleLabel } from '../rules/components/RuleLabel';
import { SxStyles } from '../shared/utils/types';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useCurrencies } from '../currency/CurrenciesContext';
import { useFeatures } from '../features/FeaturesContext';
import { useLanguages } from '../languages/LanguagesContext';
import { useRewards } from '../rewards/RewardsContext';

const styles: SxStyles = {
  iconButton: {
    color: colorPalette.grey.main,
    backgroundColor: 'inherit',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: 'inherit',
      color: colorPalette.grey.darkHover,
    },
  },
};

interface BurningRuleTableRowProps {
  rule: RuleAttributes;
  dragHandleProps?: DraggableProvidedDragHandleProps | null | undefined;
  onCloned: () => void;
  onDeleted: () => void;
  draggableRef: Ref<any>;
  draggableProps: DraggableProvidedDraggableProps;
}

function getCurrencyData(rule: RuleAttributes): {
  currencyId: string | undefined;
  amount: number | undefined;
} {
  const impact = (rule.actions.find(a => a.kind === ActionTypes.TRANSACTION) as TransactionAction)
    ?.impact as TransactionFixedImpact;
  return { currencyId: impact?.currency, amount: impact?.value };
}

export function BurningRuleTableRow({
  rule,
  onCloned,
  onDeleted,
  dragHandleProps,
  draggableRef,
  draggableProps,
}: BurningRuleTableRowProps) {
  const { t } = useTranslation();
  const [conflictModalIsOpen, setConflictModalIsOpen] = useState(false);
  const [conflictDialogProps, setConflictDialogProps] = useState<
    Omit<RuleConflictModalProps, 'isOpen' | 'setIsOpen'>
  >({
    ruleType: RuleType.BURN,
  });
  const { currencies } = useCurrencies();
  const { features } = useFeatures();
  const { write: ruleWrite, remove: ruleRemove } = features[Feature.BURNING_RULE];

  const [editModalOpen, setEditModalOpen] = useState(false);

  const handleEdit = () => setEditModalOpen(true);

  const { ruleApi } = useApi();
  const { notify } = useAlerts();
  const { isPending: isDeleting, mutate: deleteRule } = useMutation({
    mutationKey: ['rule.delete'],

    mutationFn: () =>
      ruleApi
        .deleteRule(rule.id, rule.version)
        .then(onDeleted)
        .catch(error => {
          if (error.response?.status === 409) {
            setConflictDialogProps({
              ...conflictDialogProps,
              lastModification: error.response?.data?.details,
            });
            setConflictModalIsOpen(true);
          } else {
            throw error;
          }
        }),

    onError: (err: any) => notify(extractErrorMessage(err) || t('error.page'), 'error'),
  });
  const { isPending: isCloning, mutate: cloneRule } = useMutation({
    mutationKey: ['rule', rule, 'clone'],
    mutationFn: () =>
      ruleApi.addRule(rule.programId, { ...rule, name: `${rule.name} (copy)` }).then(onCloned),
    onError: (err: any) => notify(extractErrorMessage(err) || t('error.page'), 'error'),
  });

  const { rewards } = useRewards();

  const rewardId = (
    rule.actions.find(a => a.kind === ActionTypes.REWARD_ATTRIBUTION) as ObtainRewardAction
  )?.impact.rewardId;
  const reward = rewardId ? rewards.find(r => r.id === rewardId) : undefined;
  const { currentLanguage } = useLanguages();
  const { currencyId } = getCurrencyData(rule);
  const currency = currencyId ? currencies.find(c => c.id === currencyId) : undefined;

  const { confirmationDialogState, setConfirmationDialogState } = useConfirmationDialog();
  const openDialog = () => {
    setConfirmationDialogState({
      title: t('rules.confirm.deletion.title'),
      content: t('rules.confirm.deletion.message'),
      open: true,
      confirmButtonText: t('rules.confirm.deletion.confirmation'),
      cancelButtonText: t('generic.actions.cancel'),
      onConfirm: () => deleteRule(),
    });
  };

  return (
    <>
      <RuleConflictModal
        {...conflictDialogProps}
        setIsOpen={setConflictModalIsOpen}
        isOpen={conflictModalIsOpen}
      />
      <TableRow ref={draggableRef} {...draggableProps}>
        <TableCell>
          {ruleWrite && (
            <Box sx={styles.iconButton} {...dragHandleProps}>
              <FontAwesomeIcon icon={faGripVertical} />
            </Box>
          )}
        </TableCell>
        <TableCell>
          <RuleLabel rule={rule} />
        </TableCell>
        <TableCell>
          {reward && <>{getLocalizedProperty(reward, 'name', currentLanguage.isoCode)}</>}
        </TableCell>
        <TableCell>
          {currency && (
            <Box display="flex" alignItems="center">
              <CurrencyImage key={currency.id} currency={currency} sx={{ marginRight: 0.5 }} />
            </Box>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex' }}>
            <EditIconButton onClick={handleEdit} disabled={!ruleWrite} />
            {isCloning ? (
              <Loader />
            ) : (
              <CopyIconButton onClick={() => cloneRule()} disabled={!ruleWrite} />
            )}
            <QlfConfirmationDialog {...confirmationDialogState} />
            {!isDeleting ? (
              <DeleteIconButton disabled={!ruleRemove} onClick={openDialog} />
            ) : (
              <Loader />
            )}
          </Box>
        </TableCell>
        {/*TODO probably more efficient to have a single edit modal instance and just swap the input rule, but this will work for now*/}
        <BurningRuleModal rule={rule} isOpen={editModalOpen} setIsOpen={setEditModalOpen} />
      </TableRow>
    </>
  );
}
